import {
  CardAction,
  CardEvents,
  CardFacts,
  CardTimeline,
  type CardEvent,
  type CardTimelineData,
  ClickableOrderNumber,
  type OnCardAction,
} from '../order-card/CardParts';
import { useCardPhrases } from '../order-card/CardLanguage';
import {
  formatDateWindow,
  formatTrackingNumber,
  updatedLabel,
} from '../order-card/order-card-format';

export type OrderTrackingCardData = {
  orderNumber: string;
  timeline?: CardTimelineData;
  statusText?: string;
  empty?: { title?: string; body?: string; eta?: string };
  facts?: {
    method?: string;
    windowStart?: string;
    windowEnd?: string;
    trackingNumber?: string;
  };
  trackingUrl?: string | null;
  events?: CardEvent[];
  updatedAt?: string;
};

// Samma kort som live-widgetens renderOrderTrackingCard (chat_embed.js).
// Tomma läget är normalläget de första timmarna efter skick — lugnande besked,
// aldrig ett tomt kort.
export function OrderTrackingCard({
  data,
  onAction,
}: {
  data: OrderTrackingCardData;
  onAction?: OnCardAction;
}) {
  const phrases = useCardPhrases();
  const factRows: Array<[string, string]> = [];
  if (data.facts?.method) factRows.push([phrases.factMethod, String(data.facts.method)]);
  const window = formatDateWindow(data.facts?.windowStart, data.facts?.windowEnd, phrases.locale);
  if (window) factRows.push([phrases.factWindow, window]);
  if (data.facts?.trackingNumber) {
    factRows.push([phrases.factTracking, formatTrackingNumber(data.facts.trackingNumber)]);
  }

  const empty = data.empty && typeof data.empty === 'object' ? data.empty : null;

  return (
    <article className="ai-chat__card ai-chat__card--tracking">
      <div className="ai-chat__card-head">
        <div className="ai-chat__card-title">{phrases.trackingTitle}</div>
        <div className="ai-chat__card-aside">
          <ClickableOrderNumber
            orderNumber={data.orderNumber}
            className="ai-chat__card-aside-link"
            onAction={onAction}
          />
        </div>
      </div>
      <CardTimeline timeline={data.timeline} />
      {empty ? (
        <div className="ai-chat__card-lede">
          <div className="ai-chat__card-status">{empty.title || data.statusText}</div>
          {empty.body ? <div>{empty.body}</div> : null}
          {empty.eta ? <div>{empty.eta}</div> : null}
        </div>
      ) : data.statusText ? (
        <div className="ai-chat__card-lede">
          <div className="ai-chat__card-status">{data.statusText}</div>
        </div>
      ) : null}
      <CardFacts rows={factRows} />
      <CardEvents events={data.events} />
      <div className="ai-chat__card-foot">
        <div className="ai-chat__card-sum ai-chat__card-sum--muted">
          {updatedLabel(data.updatedAt, Date.now(), phrases)}
        </div>
        <CardAction
          label={phrases.trackCarrier}
          url={data.trackingUrl ?? ''}
          quietLabel={phrases.noTrackingLink}
        />
      </div>
    </article>
  );
}
